import { createReducer, on } from '@ngrx/store';
import { categoryMapping } from '@pages/jobs/utils/jobs-detail.util';

import { IJob } from '@gen';
import { EducationalLevelEnum } from '@utils/enum/educational-level.enum';
import {
  bankingJobKsasFetchSuccess,
  customPathSurveySelected,
  degreeProgramOccupationsFetchSuccess,
  fetchOccupationsSuccess,
  filterSet,
  interestListFetch,
  interestListFetchFail,
  interestListFetchSuccess,
  interestSurveyAnswer,
  interestSurveyAnswerCorrect,
  interestSurveyReset,
  interestSurveySubmitSuccess,
  jobFamilyOccupationsFetchSuccess,
  jobListFetch,
  jobListFetchFail,
  jobListFetchSuccess,
  lastInterestMatchedJobsSet,
  lastSkillMatchedJobsSet,
  majorsSet,
  militaryOccupationsFetchSuccess,
  occupationSelect,
  occupationsFetchSuccess,
  occupationsSet,
  resetJobMatchState,
  resetMilitarySurvey,
  resetSkillsSurvey,
  skillSurveySubmit
} from './actions';
import { getSkillsOccupation } from './selectors';
import { computeResetJobMatchState } from './reset-job-match-state.helper';
import { DegreeProgramModel } from '@pages/job-match/surveys/skills-survey/educations-form/degree-program-auto-complete/model/option-item.model';
import { OccupationOptionModel } from '../surveys/skills-survey/work-experience-form/work-experience-auto-complete/model/occupation-option.model';

const reviewTexts = new Map([
  ['Construct a stage for a concert', 'Building and Constructing'],
  ['Develop a new vaccine', 'Investigating and Testing'],
  ['Write books or plays', 'Creative Writing'],
  ['Help friends with personal problems', 'Building Interpersonal Relationships'],
  ['Lead and manage a large group project', 'Guiding and Directing Others'],
  ['Set up a wifi network for your family', 'Installing Equipment'],
  ['Repair a car engine', 'Repairing and Maintaining Equipment'],
  ['Study ways to reduce water pollution', 'Getting Information'],
  ['Compose or arrange music', 'Thinking Creatively'],
  ['Help a friend pick the right college', 'Providing Consultation and Advice to Others'],
  ['Start your own business', 'Developing Strategies and Objectives'],
  ['Build a spreadsheet to track your budget', 'Creating Systems and Processes'],
  ['Build your own computer', 'Assembling Equipment and Parts'],
  ['Conduct chemical experiments', 'Analyzing Data or Information'],
  ['Create special effects for movies', 'Creative Visualization'],
  ['Work with special needs kids or adults', 'Assisting and Caring for Others'],
  ['Negotiate business contracts', 'Resolving Conflicts and Negotiating with Others'],
  ['Organize the apps on your phone', 'Organizing Information'],
  ['Work for Doordash or Uber Eats', 'Serving Others'],
  ['Monitor trends in the media', 'Interpreting the Meaning of Information'],
  ['Design social media posts for a new brand', 'Developing Creative Strategies'],
  ['Do volunteer work', 'Giving Time and Attention to Others'],
  ['Market a new line of clothing', 'Selling or Influencing Others'],
  ['Inventory supplies using your phone', 'Processing Information'],
  ['Test several recipes for a single food dish', 'Judging Quality'],
  ['Develop a weather prediction app', 'Monitoring Processes, Materials or Surroundings'],
  ['Write scripts for movies or television shows', 'Creative Storytelling'],
  ['Tutor your classmates', 'Instructing and Teaching Others'],
  ['Work at store in the mall ', 'Working and Communicating with the Public'],
  ['Organize books or music into different categories', 'Categorizing Information']
]);

export interface JobListItem extends IJob {
  interestScore?: number;
}

export interface JobMatchState {
  jobsLoading: boolean;
  jobs: JobListItem[];
  occupations: any[];
  lastInterestMatchedJobs: JobListItem[];
  userTraitScores: Record<number, number>;
  filter: string;
  interestsLoading: boolean;
  interests: any[];
  reviewInterests?: any[];
  interestsSurvey: {
    currentQuestionIndex: number;
    takeOnce: boolean;
  };
  customPathSurvey: {
    selectedSurveyIndex: number;
  };
  skills: any[];
  skillsSurvey: {
    occupations: OccupationOptionModel[];
    majors: {
      degreeProgram: DegreeProgramModel;
      educationLevel?: EducationalLevelEnum;
    }[];
    occupationsDetails: any[];
    selectedOccupation: string | null;
    takeOnce: boolean;
  };
  jobFamilyOccupations: any[];
  degreeProgramOccupations: any[];
  educationLevelOccupations: any[];
  militaryOccupations: any[];
  // KSA data for the banking-job corpus, fetched once after jobListFetchSuccess.
  // Keyed by banking O*NET code; value is the `occupationKSAs` array from the
  // CPB query. Used by the KSA-overlap synthetic experience source.
  bankingJobKsas: Record<string, any[]>;
}

export const initialState: JobMatchState = {
  jobsLoading: true,
  jobs: [],
  occupations: null,
  lastInterestMatchedJobs: [],
  userTraitScores: {},
  filter: 'best-match',
  interestsLoading: false,
  interests: [],
  interestsSurvey: {
    currentQuestionIndex: 0,
    takeOnce: false
  },
  customPathSurvey: {
    selectedSurveyIndex: NaN
  },
  skills: [],
  skillsSurvey: {
    occupations: [],
    majors: [],
    occupationsDetails: [],
    selectedOccupation: null,
    takeOnce: false
  },
  jobFamilyOccupations: [],
  degreeProgramOccupations: [],
  educationLevelOccupations: [],
  militaryOccupations: [],
  bankingJobKsas: {}
};

export const jobMatchReducer = createReducer(
  initialState,

  on(jobListFetch, state => ({ ...state, jobsLoading: true })),
  on(jobListFetchSuccess, (state, { data }) => ({
    ...state,
    jobsLoading: false,
    jobs: data
  })),
  on(jobListFetchFail, state => ({ ...state, jobsLoading: false })),

  on(bankingJobKsasFetchSuccess, (state, { data }) => ({
    ...state,
    bankingJobKsas: data
  })),

  on(interestListFetch, state => ({ ...state, interestsLoading: true })),
  on(interestListFetchSuccess, (state, { data }) => ({
    ...state,
    interestsLoading: false,
    interests: data.map(item => ({
      ...item,
      reviewText: reviewTexts.get(item.question)
    })),
    interestsSurvey: {
      currentQuestionIndex: 0,
      takeOnce: false
    }
  })),
  on(interestListFetchFail, state => ({ ...state, interestsLoading: false })),

  on(interestSurveyAnswer, (state, { answer }) => ({
    ...state,
    interests: state.interests.map((q, index) => {
      if (index === state.interestsSurvey.currentQuestionIndex) {
        return { ...q, answer };
      }
      return q;
    }),
    interestsSurvey: {
      ...state.interestsSurvey,
      currentQuestionIndex: state.interestsSurvey.currentQuestionIndex + 1
    }
  })),
  on(lastInterestMatchedJobsSet, (state, { interests, lastInterestMatchedJobs }) => {
    const tmpInterests = interests.length ? interests : state.interests.map(value => ({ ...value }));
    const tmpLastInterestMatchedJobs = lastInterestMatchedJobs.length
      ? lastInterestMatchedJobs
      : state.lastInterestMatchedJobs.map(value => ({ ...value }));
    return {
      ...state,
      interests: tmpInterests,
      lastInterestMatchedJobs: tmpLastInterestMatchedJobs
    };
  }),
  on(
    lastSkillMatchedJobsSet,
    (
      state,
      {
        skills,
        skillsSurvey,
        jobFamilyOccupations,
        degreeProgramOccupations,
        educationLevelOccupations,
        militaryOccupations
      }
    ) => ({
      ...state,
      skills: skills.length ? skills : state.skills,
      skillsSurvey: {
        occupations: skillsSurvey.occupations.length ? skillsSurvey.occupations : state.skillsSurvey.occupations,
        majors: skillsSurvey.majors.length ? skillsSurvey.majors : state.skillsSurvey.majors,
        occupationsDetails: skillsSurvey.occupationsDetails.length
          ? skillsSurvey.occupationsDetails
          : state.skillsSurvey.occupationsDetails,
        selectedOccupation: skillsSurvey.selectedOccupation
          ? skillsSurvey.selectedOccupation
          : state.skillsSurvey.selectedOccupation,
        takeOnce: skillsSurvey.takeOnce ? skillsSurvey.takeOnce : state.skillsSurvey.takeOnce
      },
      jobFamilyOccupations: jobFamilyOccupations.length ? jobFamilyOccupations : state.jobFamilyOccupations,
      degreeProgramOccupations: degreeProgramOccupations.length
        ? degreeProgramOccupations
        : state.degreeProgramOccupations,
      educationLevelOccupations: educationLevelOccupations.length
        ? educationLevelOccupations
        : state.educationLevelOccupations,
      militaryOccupations: militaryOccupations.length ? militaryOccupations : state.militaryOccupations
    })
  ),
  on(interestSurveySubmitSuccess, (state, { scoreMap, degenerate, userTraitScores }) => ({
    ...state,
    userTraitScores,
    // When input is degenerate (no SMILEs and MEHs < 50% of total) we write
    // an empty matched-jobs list. The selector that powers the results page
    // reads `lastInterestMatchedJobs` for the interest-match filter, so an
    // empty array yields zero result cards and triggers the existing empty
    // state in results.component.html. This is intentional: a user who said
    // "I'd enjoy zero things" should not be told 20 careers are an Ok fit.
    lastInterestMatchedJobs: degenerate
      ? []
      : state.jobs.map(job => ({
          ...job,
          interestScore: scoreMap[job.id]
        })),
    reviewInterests: state.interests
      .filter(q => q.answer === 'SMILE')
      .map(question => ({
        ...question,
        category: categoryMapping.get(question.reviewText)
      }))
  })),

  on(customPathSurveySelected, (state, { selectedSurveyIndex }) => ({
    ...state,
    customPathSurvey: {
      ...state.customPathSurvey,
      selectedSurveyIndex
    }
  })),

  on(filterSet, (state, { filter }) => ({
    ...state,
    filter
  })),

  on(occupationsSet, (state, { occupations }) => ({
    ...state,
    skillsSurvey: {
      ...state.skillsSurvey,
      occupations,
      selectedOccupation: occupations?.length && occupations[0].id
    }
  })),

  on(majorsSet, (state, { majors }) => ({
    ...state,
    skillsSurvey: {
      ...state.skillsSurvey,
      majors
    }
  })),

  on(occupationsFetchSuccess, (state, { data }) => ({
    ...state,
    skillsSurvey: {
      ...state.skillsSurvey,
      // Preserve raw `occupationKSAs` under a parallel field so the KSA-overlap
      // similarity computation (in the skills-match selector) has the unmodified
      // `{ ksa: {id, ...}, importance, level }` shape with all 161 KSAs intact,
      // including Work Activities. The existing `occupationKSAs` field stays
      // pre-filtered/flattened for UI display consumers (KSA list on detail pages).
      occupationsDetails: data.map(occupation => ({
        ...occupation,
        rawOccupationKSAs: occupation.occupationKSAs,
        occupationKSAs: occupation.occupationKSAs
          ?.filter(item => item.importance >= 3.0)
          .map(subItem => subItem.ksa)
          .filter(ksa => ksa.type !== 'Work Activity')
      }))
    }
  })),

  on(occupationSelect, (state, { id }) => ({
    ...state,
    skillsSurvey: {
      ...state.skillsSurvey,
      selectedOccupation: id
    }
  })),

  on(jobFamilyOccupationsFetchSuccess, (state, { data }) => ({
    ...state,
    jobFamilyOccupations: data
  })),

  on(degreeProgramOccupationsFetchSuccess, (state, { data }) => ({
    ...state,
    degreeProgramOccupations: data
  })),

  on(fetchOccupationsSuccess, (state, { occupations }) => ({
    ...state,
    occupations
  })),

  on(militaryOccupationsFetchSuccess, (state, { data }) => ({
    ...state,
    militaryOccupations: (data || []).map(occupation => ({
      ...occupation,
      // Preserve the full raw `{ ksa: {id}, importance, level }` shape (with
      // all 161 KSAs including Work Activities) under a parallel field so the
      // TF-IDF KSA-overlap source can compute cosines against the banking
      // corpus. Mirrors the civilian `occupationsFetchSuccess` handler.
      // Without this, military-only users never produce any KSA-overlap
      // greatFit because mergeExperienceUserOccupations reads rawOccupationKSAs.
      rawOccupationKSAs: occupation.occupationKSAs,
      occupationKSAs: occupation.occupationKSAs
        ?.filter(item => item.importance >= 3.0)
        .map(subItem => subItem.ksa)
        .filter(ksa => ksa.type !== 'Work Activity')
    }))
  })),

  on(interestSurveyAnswerCorrect, (state, { questionId, answer }) => ({
    ...state,
    interests: state.interests.map(interest => {
      if (interest.questionId === questionId) {
        return { ...interest, answer };
      }
      return interest;
    })
  })),

  on(skillSurveySubmit, state => ({
    ...state,
    skills: getSkillsOccupation(state.skillsSurvey.occupationsDetails, state.skillsSurvey.selectedOccupation).concat(
      getSkillsOccupation(state.militaryOccupations, state.militaryOccupations[0]?.onetCode)
    ),
    skillsSurvey: { ...state.skillsSurvey, takeOnce: true }
  })),
  // Start Over: clear survey state but keep the loaded jobs reference data.
  // See reset-job-match-state.helper.ts for the full bug context — preserving
  // jobsLoading is what keeps <app-results> from unmounting after Start Over.
  on(resetJobMatchState, state => computeResetJobMatchState(initialState, state)),
  on(resetMilitarySurvey, state => ({
    ...state,
    militaryOccupations: [],
    skillsSurvey: {
      ...state.skillsSurvey,
      occupations: [],
      occupationsDetails: [],
      selectedOccupation: null
    }
  })),
  on(resetSkillsSurvey, state => ({
    ...state,
    militaryOccupations: [],
    skillsSurvey: {
      ...initialState.skillsSurvey
    },
    jobFamilyOccupations: [],
    degreeProgramOccupations: [],
    educationLevelOccupations: []
  })),
  on(interestSurveyReset, state => ({
    ...state,
    interests: [],
    lastInterestMatchedJobs: [],
    userTraitScores: {},
    reviewInterests: state.interests
      .filter(q => q.answer === 'SMILE')
      .map(question => ({
        ...question,
        category: categoryMapping.get(question.reviewText)
      })),
    interestsSurvey: {
      currentQuestionIndex: 0,
      takeOnce: false
    }
  }))
);
